/**
 * The dice tray: simulated 3D dice that tumble and land on the values the server rolled.
 *
 * No dependencies. Each die is a real convex polyhedron (built from its vertices with a small
 * hull routine), rotated with quaternions and drawn on a 2D canvas with back-face culling and
 * flat shading. The landing orientation is solved so the rolled face points at the viewer, then
 * the tumble is a slerp from a random orientation with decaying extra spin.
 */
import type { RollView } from '../shared/view.ts';

type V3 = [number, number, number];
type Quat = [number, number, number, number]; // x, y, z, w

const sub = (a: V3, b: V3): V3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const add = (a: V3, b: V3): V3 => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
const scale = (a: V3, s: number): V3 => [a[0] * s, a[1] * s, a[2] * s];
const dot = (a: V3, b: V3) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const cross = (a: V3, b: V3): V3 => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
const len = (a: V3) => Math.hypot(a[0], a[1], a[2]);
const norm = (a: V3): V3 => scale(a, 1 / (len(a) || 1));

function qMul(a: Quat, b: Quat): Quat {
  const [ax, ay, az, aw] = a, [bx, by, bz, bw] = b;
  return [aw * bx + ax * bw + ay * bz - az * by, aw * by - ax * bz + ay * bw + az * bx, aw * bz + ax * by - ay * bx + az * bw, aw * bw - ax * bx - ay * by - az * bz];
}

function qAxis(axis: V3, angle: number): Quat {
  const s = Math.sin(angle / 2);
  const n = norm(axis);
  return [n[0] * s, n[1] * s, n[2] * s, Math.cos(angle / 2)];
}

export function qRotate(q: Quat, v: V3): V3 {
  const [x, y, z, w] = q;
  const u: V3 = [x, y, z];
  const t = scale(cross(u, v), 2);
  return add(add(v, scale(t, w)), cross(u, t));
}

function qSlerp(a: Quat, b: Quat, t: number): Quat {
  let d = a[0] * b[0] + a[1] * b[1] + a[2] * b[2] + a[3] * b[3];
  let bb = b;
  if (d < 0) {
    d = -d;
    bb = [-b[0], -b[1], -b[2], -b[3]];
  }
  if (d > 0.9995) {
    const r: Quat = [a[0] + (bb[0] - a[0]) * t, a[1] + (bb[1] - a[1]) * t, a[2] + (bb[2] - a[2]) * t, a[3] + (bb[3] - a[3]) * t];
    const l = Math.hypot(...r);
    return [r[0] / l, r[1] / l, r[2] / l, r[3] / l];
  }
  const th = Math.acos(d);
  const s = Math.sin(th);
  const wa = Math.sin((1 - t) * th) / s, wb = Math.sin(t * th) / s;
  return [a[0] * wa + bb[0] * wb, a[1] * wa + bb[1] * wb, a[2] * wa + bb[2] * wb, a[3] * wa + bb[3] * wb];
}

/** Rotation whose rows are the given orthonormal basis (maps r→x, u→y, n→z). */
function qFromRows(r: V3, u: V3, n: V3): Quat {
  const m00 = r[0], m01 = r[1], m02 = r[2], m10 = u[0], m11 = u[1], m12 = u[2], m20 = n[0], m21 = n[1], m22 = n[2];
  const tr = m00 + m11 + m22;
  let q: Quat;
  if (tr > 0) {
    const s = Math.sqrt(tr + 1) * 2;
    q = [(m21 - m12) / s, (m02 - m20) / s, (m10 - m01) / s, s / 4];
  } else if (m00 > m11 && m00 > m22) {
    const s = Math.sqrt(1 + m00 - m11 - m22) * 2;
    q = [s / 4, (m01 + m10) / s, (m02 + m20) / s, (m21 - m12) / s];
  } else if (m11 > m22) {
    const s = Math.sqrt(1 + m11 - m00 - m22) * 2;
    q = [(m01 + m10) / s, s / 4, (m12 + m21) / s, (m02 - m20) / s];
  } else {
    const s = Math.sqrt(1 + m22 - m00 - m11) * 2;
    q = [(m02 + m20) / s, (m12 + m21) / s, s / 4, (m10 - m01) / s];
  }
  const l = Math.hypot(...q);
  return [q[0] / l, q[1] / l, q[2] / l, q[3] / l];
}

function randomQuat(): Quat {
  const u1 = Math.random(), u2 = Math.random() * Math.PI * 2, u3 = Math.random() * Math.PI * 2;
  const a = Math.sqrt(1 - u1), b = Math.sqrt(u1);
  return [a * Math.sin(u2), a * Math.cos(u2), b * Math.sin(u3), b * Math.cos(u3)];
}

// ------------------------------------------------------------ polyhedra

interface Face {
  idx: number[];
  normal: V3;
  center: V3;
  up: V3;
  right: V3;
  value: number;
}

interface Solid {
  sides: number;
  verts: V3[];
  faces: Face[];
  textScale: number;
}

/** Faces of a convex polyhedron centred on the origin, vertices ordered counter-clockwise. */
function hullFaces(verts: V3[]): number[][] {
  const planes: { n: V3; d: number; idx: number[] }[] = [];
  const eps = 1e-5;
  for (let i = 0; i < verts.length; i++) {
    for (let j = i + 1; j < verts.length; j++) {
      for (let k = j + 1; k < verts.length; k++) {
        let n = cross(sub(verts[j], verts[i]), sub(verts[k], verts[i]));
        if (len(n) < eps) continue;
        n = norm(n);
        let d = dot(n, verts[i]);
        if (d < 0) {
          n = scale(n, -1);
          d = -d;
        }
        if (verts.some((v) => dot(n, v) > d + eps)) continue;
        if (planes.some((p) => dot(p.n, n) > 1 - 1e-6)) continue;
        planes.push({ n, d, idx: verts.map((v, vi) => (Math.abs(dot(n, v) - d) < eps ? vi : -1)).filter((vi) => vi >= 0) });
      }
    }
  }
  return planes.map(({ n, idx }) => {
    const c = scale(idx.reduce((s, vi) => add(s, verts[vi]), [0, 0, 0] as V3), 1 / idx.length);
    const u = norm(sub(verts[idx[0]], c));
    const w = cross(n, u);
    return [...idx].sort((a, b) => {
      const pa = sub(verts[a], c), pb = sub(verts[b], c);
      return Math.atan2(dot(pa, w), dot(pa, u)) - Math.atan2(dot(pb, w), dot(pb, u));
    });
  });
}

function buildSolid(sides: number, raw: V3[], textScale: number): Solid {
  const r = Math.max(...raw.map(len));
  const verts = raw.map((v) => scale(v, 1 / r));
  const faces = hullFaces(verts).map((idx, i) => {
    const center = scale(idx.reduce((s, vi) => add(s, verts[vi]), [0, 0, 0] as V3), 1 / idx.length);
    // The plane normal (not the centroid direction, which differs for the d10's kites).
    let normal = norm(cross(sub(verts[idx[1]], verts[idx[0]]), sub(verts[idx[2]], verts[idx[0]])));
    if (dot(normal, center) < 0) normal = scale(normal, -1);
    const dists = idx.map((vi) => len(sub(verts[vi], center)));
    const square = idx.length === 4 && Math.max(...dists) - Math.min(...dists) < 1e-4;
    const anchor = square
      ? scale(add(verts[idx[0]], verts[idx[1]]), 0.5)
      : verts[idx[dists.indexOf(Math.max(...dists))]];
    const up = norm(sub(anchor, center));
    return { idx, normal, center, up, right: norm(cross(up, normal)), value: i + 1 };
  });
  return { sides, verts, faces, textScale };
}

const PHI = (1 + Math.sqrt(5)) / 2;

function d10Verts(): V3[] {
  const z0 = (1 - Math.cos(Math.PI / 5)) / (1 + Math.cos(Math.PI / 5));
  const ring: V3[] = Array.from({ length: 10 }, (_, k) => [Math.cos((k * Math.PI) / 5), Math.sin((k * Math.PI) / 5), k % 2 ? -z0 : z0]);
  return [[0, 0, 1], [0, 0, -1], ...ring];
}

const SOLIDS: Record<number, Solid> = {
  4: buildSolid(4, [[1, 1, 1], [1, -1, -1], [-1, 1, -1], [-1, -1, 1]], 0.5),
  6: buildSolid(6, [-1, 1].flatMap((x) => [-1, 1].flatMap((y) => [-1, 1].map((z) => [x, y, z] as V3))), 0.62),
  8: buildSolid(8, [[1, 0, 0], [-1, 0, 0], [0, 1, 0], [0, -1, 0], [0, 0, 1], [0, 0, -1]], 0.5),
  10: buildSolid(10, d10Verts(), 0.42),
  12: buildSolid(12, [
    ...[-1, 1].flatMap((x) => [-1, 1].flatMap((y) => [-1, 1].map((z) => [x, y, z] as V3))),
    ...[-1, 1].flatMap((a) => [-1, 1].flatMap((b) => [[0, a / PHI, b * PHI], [a / PHI, b * PHI, 0], [b * PHI, 0, a / PHI]] as V3[])),
  ], 0.42),
  20: buildSolid(20, [-1, 1].flatMap((a) => [-1, 1].flatMap((b) => [[0, a, b * PHI], [a, b * PHI, 0], [b * PHI, 0, a]] as V3[])), 0.36),
};

export function solidFor(sides: number): Solid {
  return SOLIDS[sides] ?? SOLIDS[6];
}

/** Orientation that shows `face` to the viewer, upright, tilted slightly by `tilt` radians. */
export function landingQuat(face: Face, tilt = 0): Quat {
  return qMul(qAxis([0, 0, 1], tilt), qFromRows(face.right, face.up, face.normal));
}

// ------------------------------------------------------------ the tray

interface DieAnim {
  solid: Solid;
  value: number;
  face: Face;
  qStart: Quat;
  qEnd: Quat;
  spinAxis: V3;
  spins: number;
  from: [number, number];
  to: [number, number];
  size: number;
  delay: number;
}

interface Palette {
  body: [number, number, number];
  ink: string;
  edge: string;
}

function paletteFor(roll: RollView): Palette {
  if (roll.kind === 'spell') return { body: [96, 66, 168], ink: '#ffe08a', edge: '#2b1d4d' };
  if (roll.kind === 'flat' || roll.kind === 'recovery') return { body: [88, 100, 112], ink: '#f2f2f2', edge: '#262c32' };
  if (roll.kind === 'encounter' || roll.kind === 'rest') return { body: [92, 108, 74], ink: '#f3ead2', edge: '#273021' };
  if (roll.side === 'hostile') return { body: [150, 34, 34], ink: '#f6e7d0', edge: '#3d0c0c' };
  return { body: [236, 226, 204], ink: '#2b2118', edge: '#6e5b42' };
}

const LIGHT = norm([-0.45, 0.6, 0.75]);
const easeOut = (t: number) => 1 - (1 - t) ** 3;

function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}

/** One animated pane of the tray: a label, a canvas the dice tumble on, and the result. */
class DiceStage {
  readonly el: HTMLElement;
  private readonly canvas: HTMLCanvasElement;
  private readonly g: CanvasRenderingContext2D;
  private readonly labelEl: HTMLElement;
  private readonly resultEl: HTMLElement;
  current: { roll: RollView; dice: DieAnim[]; start: number; duration: number; hold: number; palette: Palette; finished: boolean } | null = null;

  constructor(el: HTMLElement, placeholder: string) {
    this.el = el;
    el.innerHTML = `<div class="dice-label muted">${esc(placeholder)}</div><canvas class="dice-canvas"></canvas><div class="dice-result"></div>`;
    this.canvas = el.querySelector('canvas')!;
    this.g = this.canvas.getContext('2d')!;
    this.labelEl = el.querySelector('.dice-label')!;
    this.resultEl = el.querySelector('.dice-result')!;
  }

  /** When this stage is free for the next roll. */
  busyUntil(): number {
    const cur = this.current;
    return cur ? cur.start + (cur.dice.at(-1)?.delay ?? 0) + cur.duration + cur.hold : 0;
  }

  landingIn(now: number): number {
    const cur = this.current;
    return cur && !cur.finished ? Math.max(0, cur.start + (cur.dice.at(-1)?.delay ?? 0) + cur.duration - now) : 0;
  }

  clear(placeholder: string): void {
    this.current = null;
    this.labelEl.className = 'dice-label muted';
    this.labelEl.textContent = placeholder;
    this.labelEl.removeAttribute('title');
    this.resultEl.innerHTML = '';
    const dpr = window.devicePixelRatio || 1;
    this.g.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.g.clearRect(0, 0, this.canvas.clientWidth, this.canvas.clientHeight);
  }

  start(roll: RollView, now: number, fast: boolean): void {
    const dpr = window.devicePixelRatio || 1;
    const w = this.canvas.clientWidth, h = this.canvas.clientHeight;
    if (this.canvas.width !== Math.round(w * dpr) || this.canvas.height !== Math.round(h * dpr)) {
      this.canvas.width = Math.round(w * dpr);
      this.canvas.height = Math.round(h * dpr);
    }
    const shown = roll.dice.slice(0, 12);
    const n = Math.max(1, shown.length);
    const size = n === 1 ? 32 : n === 2 ? 26 : n <= 4 ? 20 : n <= 8 ? 15 : 11;
    const perRow = Math.max(1, Math.floor((w - 12) / (size * 2.3)));
    const rows = Math.ceil(n / perRow);
    const dice = shown.map((d, i): DieAnim => {
      const solid = solidFor(d.sides);
      const face = solid.faces.find((f) => f.value === d.value) ?? solid.faces[(d.value - 1) % solid.faces.length];
      const row = Math.floor(i / perRow), col = i % perRow;
      const inRow = Math.min(perRow, n - row * perRow);
      const tx = w / 2 + (col - (inRow - 1) / 2) * size * 2.3 + (Math.random() - 0.5) * size * 0.3;
      const ty = h / 2 + (row - (rows - 1) / 2) * size * 2.2 + (Math.random() - 0.5) * size * 0.3;
      return {
        solid, value: d.value, face,
        qStart: randomQuat(),
        qEnd: landingQuat(face, (Math.random() - 0.5) * 0.5),
        spinAxis: norm([Math.random() - 0.5, Math.random() - 0.5, Math.random() - 0.5]),
        spins: 1.5 + Math.random() * 1.5,
        from: [-size * 2 - Math.random() * 30, ty + (Math.random() - 0.5) * h * 0.8],
        to: [tx, ty], size, delay: i * (fast ? 25 : 55),
      };
    });
    this.current = { roll, dice, start: now, duration: fast ? 500 : 950, hold: fast ? 150 : 450, palette: paletteFor(roll), finished: false };
    this.labelEl.className = 'dice-label';
    this.labelEl.innerHTML = `<b>${esc(roll.label)}</b>${roll.detail ? ` <span class="muted">${esc(roll.detail)}</span>` : ''}`;
    this.labelEl.title = `${roll.label}${roll.detail ? ` (${roll.detail})` : ''}`;
    this.resultEl.innerHTML = '<span class="muted">rolling…</span>';
  }

  /** Draws a frame; returns the roll on the frame its dice land. */
  frame(now: number): RollView | null {
    const cur = this.current;
    if (!cur) return null;
    const dpr = window.devicePixelRatio || 1;
    const g = this.g;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.clearRect(0, 0, this.canvas.clientWidth, this.canvas.clientHeight);
    let landed = true;
    for (const d of cur.dice) {
      const raw = Math.max(0, Math.min(1, (now - cur.start - d.delay) / cur.duration));
      if (raw < 1) landed = false;
      const t = easeOut(raw);
      const bounce = Math.abs(Math.sin(raw * Math.PI * 2.5)) * (1 - raw) ** 2;
      const x = d.from[0] + (d.to[0] - d.from[0]) * t;
      const floorY = d.from[1] + (d.to[1] - d.from[1]) * t;
      const q = qMul(qAxis(d.spinAxis, d.spins * Math.PI * 2 * (1 - t)), qSlerp(d.qStart, d.qEnd, t));
      this.drawDie(d, q, x, floorY - bounce * d.size * 1.2, floorY, d.size * (1 + bounce * 0.25), cur.palette, raw >= 1);
    }
    if (landed && !cur.finished) {
      cur.finished = true;
      this.resultEl.innerHTML = rollMath(cur.roll);
      return cur.roll;
    }
    return null;
  }

  private drawDie(d: DieAnim, q: Quat, x: number, y: number, floorY: number, size: number, pal: Palette, landed: boolean): void {
    const g = this.g;
    const dpr = window.devicePixelRatio || 1;
    g.fillStyle = 'rgba(0,0,0,0.35)';
    g.beginPath();
    g.ellipse(x + 3, floorY + d.size * 0.95, size * 0.8, size * 0.22, 0, 0, Math.PI * 2);
    g.fill();
    const verts = d.solid.verts.map((v) => qRotate(q, v));
    const faces = d.solid.faces
      .map((f) => ({ f, n: qRotate(q, f.normal) }))
      .filter(({ n }) => n[2] > 0.02)
      .sort((a, b) => a.n[2] - b.n[2]);
    for (const { f, n } of faces) {
      const shade = 0.42 + 0.58 * Math.max(0, dot(n, LIGHT));
      const [r, gg, b] = pal.body.map((ch) => Math.round(Math.min(255, ch * shade)));
      g.beginPath();
      f.idx.forEach((vi, i) => {
        const v = verts[vi];
        const px = x + v[0] * size, py = y - v[1] * size;
        if (i === 0) g.moveTo(px, py);
        else g.lineTo(px, py);
      });
      g.closePath();
      g.fillStyle = `rgb(${r},${gg},${b})`;
      g.fill();
      const result = landed && f === d.face;
      g.strokeStyle = result ? (d.solid.sides === 20 && d.value === 20 ? '#ffd24a' : d.solid.sides === 20 && d.value === 1 ? '#ff5a5a' : pal.edge) : pal.edge;
      g.lineWidth = result ? 2 : 1;
      g.stroke();
      if (n[2] < 0.25) continue;
      const c = qRotate(q, f.center);
      const u = qRotate(q, f.up), rt = qRotate(q, f.right);
      const label = d.solid.sides >= 8 && (f.value === 6 || f.value === 9) ? `${f.value}.` : String(f.value);
      g.setTransform(dpr * rt[0], dpr * -rt[1], dpr * -u[0], dpr * u[1], dpr * (x + c[0] * size), dpr * (y - c[1] * size));
      g.fillStyle = pal.ink;
      g.font = `bold ${Math.max(6, Math.round(size * d.solid.textScale))}px ui-sans-serif, system-ui, sans-serif`;
      g.textAlign = 'center';
      g.textBaseline = 'middle';
      g.fillText(label, 0, 0);
      g.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
  }
}

/** d20 checks tumble on the left; damage, healing and other dice on the right. */
function isCheck(r: RollView): boolean {
  return r.dice.length > 0 && r.dice.every((d) => d.sides === 20);
}

const signedPart = (n: number) => `${n >= 0 ? '+' : '−'} ${Math.abs(n)}`;

/** "16 + 6 = 22 vs Will DC 14", with the total colored by degree of success. */
export function rollMath(r: RollView): string {
  if (r.kind === 'initiative') return esc(r.detail ?? '');
  const tone = r.degree !== undefined ? `deg-${r.degree}` : r.kind === 'heal' ? 'heal' : '';
  const total = `<b class="total ${tone}">${r.total}</b>`;
  const values = r.dice.length > 8 ? [r.dice.reduce((s, d) => s + d.value, 0)] : r.dice.map((d) => d.value);
  let expr = values.join(' + ') || '0';
  if (r.modifier) expr += ` ${signedPart(r.modifier)}`;
  if (r.multiplier && r.multiplier !== 1) expr = `(${expr}) × ${r.multiplier}`;
  const plain = r.dice.length === 1 && !r.modifier && !(r.multiplier && r.multiplier !== 1);
  let text = plain ? total : `${expr} = ${total}`;
  if (r.against) text += ` <span class="vs">vs ${esc(r.against.label)} ${r.against.value}</span>`;
  const type = r.damage?.length === 1 ? r.damage[0].type : r.kind === 'heal' ? 'HP' : '';
  if (type) text += ` <span class="vs">${esc(type)}</span>`;
  return text;
}

export class DiceTray {
  private readonly root: HTMLElement;
  private readonly check: DiceStage;
  private readonly result: DiceStage;
  private readonly historyEl: HTMLElement;
  private readonly queue: RollView[] = [];
  private readonly history: RollView[] = [];
  private active: DiceStage | null = null;
  private lastSeq = -1;
  private idleSince = 0;
  /** Called when a roll starts tumbling and when its dice land (for sound effects). */
  onStart: ((roll: RollView) => void) | null = null;
  onFinish: ((roll: RollView) => void) | null = null;

  constructor(root: HTMLElement) {
    this.root = root;
    root.innerHTML = `
      <div class="dice-head"><span class="dice-title">Dice</span><button class="dice-toggle small ghost" title="Hide or show the dice">–</button></div>
      <div class="dice-panes"><div class="dice-pane check"></div><div class="dice-pane result"></div></div>
      <ol class="dice-history"><li class="muted">Rolls appear here.</li></ol>`;
    this.check = new DiceStage(root.querySelector('.dice-pane.check')!, 'Check');
    this.result = new DiceStage(root.querySelector('.dice-pane.result')!, 'Result');
    this.historyEl = root.querySelector('.dice-history')!;
    const toggle = root.querySelector<HTMLButtonElement>('.dice-toggle')!;
    const collapsed = read('crawler.diceCollapsed') === '1';
    root.classList.toggle('collapsed', collapsed);
    toggle.textContent = collapsed ? '+' : '–';
    toggle.addEventListener('click', () => {
      const now = !root.classList.contains('collapsed');
      root.classList.toggle('collapsed', now);
      toggle.textContent = now ? '+' : '–';
      write('crawler.diceCollapsed', now ? '1' : '0');
    });
  }

  /** Feed the latest rolls from the view; only unseen ones are animated. */
  sync(rolls: readonly RollView[]): void {
    if (this.lastSeq < 0) {
      this.lastSeq = rolls.at(-1)?.seq ?? 0;
      return;
    }
    for (const r of rolls) {
      if (r.seq <= this.lastSeq) continue;
      this.lastSeq = r.seq;
      this.queue.push(r);
    }
    if (this.queue.length > 10) this.queue.splice(0, this.queue.length - 10);
  }

  /** Roughly how long until every queued roll has landed, in ms. */
  backlogMs(now: number): number {
    const fast = this.queue.length > 2;
    return (this.active?.landingIn(now) ?? 0) + this.queue.length * (fast ? 650 : 1400);
  }

  frame(now: number): void {
    const busy = !!this.active && now < this.active.busyUntil();
    if (!busy && this.queue.length) {
      const next = this.queue.shift()!;
      const stage = isCheck(next) ? this.check : this.result;
      // A new check starts a new exchange: clear the previous result.
      if (stage === this.check) this.result.clear('Result');
      stage.start(next, now, this.queue.length > 1);
      this.active = stage;
      this.idleSince = 0;
      this.root.classList.add('active');
      this.onStart?.(next);
    } else if (!busy && this.active) {
      this.idleSince ||= now;
      if (now - this.idleSince > 4000) this.root.classList.remove('active');
    }
    for (const stage of [this.check, this.result]) {
      const landed = stage.frame(now);
      if (!landed) continue;
      this.onFinish?.(landed);
      this.history.unshift(landed);
      this.history.length = Math.min(this.history.length, 3);
      this.renderHistory();
    }
  }

  private renderHistory(): void {
    this.historyEl.innerHTML = this.history
      .map((r) => `<li title="${esc(`${r.label}${r.detail ? ` (${r.detail})` : ''}`)}"><span class="h-label">${esc(r.short ?? r.label)}</span><span class="h-math">${rollMath(r)}</span></li>`)
      .join('');
  }
}

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // The setting just won't persist.
  }
}
